/**
 * Typen und Hilfen der Seite „Brandfallsteuerung und Notstrom" (W2-D1, 16.10.2026).
 *
 * Die Typen folgen `GET …/elektro/sicherheitstechnik` (SicherheitstechnikEndpoints.cs,
 * Sicherheitstechniklauf.cs). Gerechnet wird beim Abruf; die Seite schreibt nichts.
 */

export const PDF_HINWEIS =
  'Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit eigener Dokumentnummer an; sonst liefert er die abgelegte Fassung (F11). '
  + 'Die Excel-Mappe legt keine Fassung an.';

export const ANNAHMEN_HINWEIS =
  'Ein Teil der Nachweise steht auf unbestätigten Vorgaben (Planungsannahmen, keine Datenblattwerte). '
  + 'Ergebnisse gegen unbestätigte Werte sind Warnungen (Stern am Status), gegen bestätigte Fehler. '
  + 'Die BMA-Werte sind seit BM-2 ein Ansatz des Eigentümers (übliche technische Größe, 20.10.2026).';

export interface StKennzahlen {
  zentralen: number; bmz: number; rwa: number; ringe: number; rwaLinien: number;
  gruppen: number; abschnitte: number; ansteuerungen: number; ohneAbschnitt: number;
  befunde: number; warnungen: number; fehler: number; aufAnnahmen: boolean;
}

export interface StSpalte {
  schluessel: string; titel: string; wirkung: string; herkunft: string | null; ohneAbschnitt: number;
}

export interface StZelle { spalte: string; anzahl: number; objekte: string[] }

export interface StZeile {
  abschnitt: string; ausloesendeGruppen: number; melder: number; gruppen: string[];
  zellen: StZelle[]; befunde: string[];
}

export interface StNotstrom {
  zentrale: string; zentralenart: string; ruhestromA: number; alarmstromA: number;
  ruhezeitH: number; alarmzeitH: number; zuschlag: number; kapazitaetErforderlichAh: number;
  bestandAh: number | null; bestandHerleitung: string | null; hoechstAhBaureihe: number | null;
  ladestromA: number; netzteilErforderlichA: number; netzteilA: number | null;
  gegenAnnahmen: boolean; status: string; befunde: string[]; rechenweg: string[];
}

export interface StRing {
  linie: string; zentrale: string; art: string; laengeM: number | null; leitungsart: string | null;
  querschnittMm2: number | null; querschnittHerleitung: string | null; teilnehmer: number;
  stromA: number; spannungsfallV: number | null; spannungEndeV: number | null;
  uRingV: number; uMinV: number; gegenAnnahmen: boolean; status: string; befunde: string[];
  rechenweg: string | null;
  /** BM-2: genau (Teilstrecken) oder verteilt (Näherung); obere Schranke mit der ganzen Last am Ende. */
  lastfall?: string | null; spannungsfallObenV?: number | null; spannungEndeObenV?: number | null;
  widerstandOhmKm?: number | null; sockelsirenen?: number; tragfaehig?: boolean;
}

/** BM-2: Ein rechnerisch negatives U_Ende ist keine Spannung - der Ring ist nicht tragfähig. */
export function spannungEnde(u: number | null | undefined): number | string | null {
  if (u === null || u === undefined) return null;
  return u < 0 ? 'nicht tragfähig' : u;
}

export interface StRwaLinie {
  linie: string; zentrale: string; laengeM: number | null; querschnittMm2: number | null;
  antriebe: number; stromA: number; spannungsfallV: number | null; spannungsfallProzent: number | null;
  zulaessigProzent: number; gegenAnnahmen: boolean; status: string; befunde: string[];
  rechenweg: string | null;
}

export interface StRwaZentrale {
  zentrale: string; baureihe: string | null; linien: number; antriebe: number;
  summeAntriebsstromA: number; hoechstAntriebsstromA: number | null; antriebsgruppen: number | null;
  befunde: string[];
}

export interface StVorgabe {
  feld: string; verwendung: string | null; wert: string; einheit: string | null;
  bestaetigt: boolean; herkunft: string; normbezug: string | null; rueckfall: boolean;
}

export interface SicherheitstechnikStand {
  kennzahlen: StKennzahlen;
  matrix: {
    spalten: StSpalte[]; zeilen: StZeile[]; befunde: string[]; gruppen: number;
    abschnitteAusGruppen: number;
  };
  notstrom: StNotstrom[];
  ringe: StRing[];
  rwaLinien: StRwaLinie[];
  rwaZentralen: StRwaZentrale[];
  vorgaben: StVorgabe[];
  befunde: string[];
  rho: number;
  stand: string;
}

export type Stufe = 'fehler' | 'warnung' | 'hinweis';

/** „WARNUNG  Text" → Stufe und Text. */
export function zerlegeBefund(befund: string): { stufe: Stufe; text: string } {
  const m = /^(BLOCKER|FEHLER|WARNUNG|HINWEIS)\s+(.*)$/s.exec(befund);
  if (!m) return { stufe: 'hinweis', text: befund };
  const stufe: Stufe = m[1] === 'HINWEIS' ? 'hinweis' : m[1] === 'WARNUNG' ? 'warnung' : 'fehler';
  return { stufe, text: m[2] };
}

/** Status des Servers ('ok' | 'warnung' | 'fehler') als Text, mit * bei Annahmen. */
export function statusText(status: string, gegenAnnahmen: boolean): string {
  const wort = status === 'fehler' ? 'Fehler' : status === 'warnung' ? 'Warnung' : 'in Ordnung';
  return gegenAnnahmen && status !== 'fehler' ? `${wort}*` : wort;
}

/** Die Anzahl einer Spalte in einer Matrixzeile. */
export function zellenzahl(zeile: StZeile, spalte: string): number {
  return zeile.zellen.find((z) => z.spalte === spalte)?.anzahl ?? 0;
}
